/**
 * Calcula los impactos efectivos de una campaña publicitaria
 * usando distribución de Poisson y datos IPSOS
 *
 * Metodología:
 * 1. Calcula la probabilidad de aparición del spot durante cada trayecto en ascensor
 * 2. Usa distribución de Poisson: P(ver >= 1) = 1 - e^(-λ)
 * 3. Acumula probabilidad por día considerando múltiples trayectos
 * 4. Ajusta por factor de atención (IPSOS) y factor de asistencia semanal
 */

import { DATOS_IPSOS } from "../constants/datosIpsos";

/**
 * Calcula los impactos efectivos para una campaña
 *
 * @param {Object} params - Parámetros de la campaña
 * @param {number} params.horasExhibicion - Horas de exhibición diaria (ej: 14, 7, 3.5)
 * @param {number} params.frecuenciaPorHora - Frecuencia de spots por hora (ej: 10, 15, 20)
 * @param {number} params.diasCampana - Días de campaña (plazo)
 * @param {number} [params.duracionTrayecto=45] - Duración de cada trayecto en segundos
 * @param {number} [params.audienciaTotal=0] - Audiencia total (personas)
 * @param {Object} [params.datosIpsos] - Datos de IPSOS (usa default si no se provee)
 * @returns {Object} Cálculos de impactos efectivos
 */
export const calculateImpactosEfectivos = ({
  horasExhibicion,
  frecuenciaPorHora,
  diasCampana,
  duracionTrayecto = 45,
  audienciaTotal = 0,
  datosIpsos = DATOS_IPSOS,
}) => {
  // Validar parámetros mínimos
  if (!horasExhibicion || !frecuenciaPorHora || !diasCampana) {
    return {
      probAparicionTrayecto: 0,
      probImpactoViaje: 0,
      probImpactoDiario: 0,
      probImpactoEfectivoDiario: 0,
      impactosPorPersonaPorDia: 0,
      impactosPorPersonaPorCampana: 0,
      impactosTotalesCampana: 0,
      factorCobertura: 0,
      spotsEsperadosPorTrayecto: 0,
    };
  }

  // Factor de cobertura: Full Day (14h) cubre todos los viajes, menos horas = menor cobertura
  const factorCobertura = horasExhibicion >= 14 ? 1 : horasExhibicion / 14;

  // Viajes y trayectos en la ventana de exhibición
  const viajesEnVentana = datosIpsos.viajesPorDia * factorCobertura;
  const trayectosEnVentana = Math.round(viajesEnVentana * datosIpsos.trayectosPorViaje);

  // Probabilidad por trayecto usando distribución Poisson
  // λ = spots esperados durante el trayecto = (frecuencia por segundo) × duración trayecto
  // P(ver al menos 1 spot) = 1 - e^(-λ)
  const spotsEsperadosPorTrayecto = (frecuenciaPorHora / 3600) * duracionTrayecto;
  const probAparicionTrayecto = 1 - Math.exp(-spotsEsperadosPorTrayecto);

  // Probabilidad de NO ver el spot en un trayecto
  const probNoVerEnTrayecto = 1 - probAparicionTrayecto;

  // Probabilidad por viaje (2 trayectos: sube + baja)
  const probNoVerEnViaje = Math.pow(probNoVerEnTrayecto, 2);
  const probImpactoViaje = 1 - probNoVerEnViaje;

  // Probabilidad por día (múltiples trayectos)
  const probNoVerEnDia = Math.pow(probNoVerEnTrayecto, trayectosEnVentana);
  const probImpactoDiario = 1 - probNoVerEnDia;

  // Ajuste por factor de atención (IPSOS: ~85.7% presta atención)
  const probImpactoEfectivoDiario = probImpactoDiario * (datosIpsos.porcentajeAtencion / 100);

  // Factor de asistencia semanal (no todos van 5 días)
  const diasPorSemana = datosIpsos.diasPorSemanaPromedio || 5;
  const factorAsistencia = diasPorSemana / 5;

  // Impactos efectivos por persona
  const impactosPorPersonaPorDia = probImpactoEfectivoDiario;
  const impactosPorPersonaPorCampana = impactosPorPersonaPorDia * diasCampana * factorAsistencia;

  // Impactos totales (considerando audiencia)
  const impactosTotalesCampana = impactosPorPersonaPorCampana * audienciaTotal;

  return {
    // Probabilidades (valores 0-1)
    probAparicionTrayecto,
    probImpactoViaje,
    probImpactoDiario,
    probImpactoEfectivoDiario,
    // Impactos
    impactosPorPersonaPorDia,
    impactosPorPersonaPorCampana,
    impactosTotalesCampana,
    // Factores auxiliares
    factorCobertura,
    factorAsistencia,
    spotsEsperadosPorTrayecto,
    trayectosEnVentana,
    viajesEnVentana,
    diasPorSemana,
  };
};

/**
 * Calcula impactos efectivos por circuito (ascensores, totems, espera, cowork)
 *
 * @param {Object} params - Parámetros del circuito
 * @param {number} params.horasExhibicion - Horas de exhibición
 * @param {number} params.frecuenciaPorHora - Frecuencia
 * @param {number} params.diasCampana - Plazo
 * @param {number} params.audiencia - Audiencia del circuito
 * @param {number} [params.duracionTrayecto=45] - Duración trayecto (puede variar por tipo)
 * @returns {number} Impactos efectivos totales del circuito
 */
export const calculateImpactosEfectivosCircuito = ({
  horasExhibicion,
  frecuenciaPorHora,
  diasCampana,
  audiencia,
  duracionTrayecto = 45,
}) => {
  const resultado = calculateImpactosEfectivos({
    horasExhibicion,
    frecuenciaPorHora,
    diasCampana,
    duracionTrayecto,
    audienciaTotal: audiencia,
  });

  return resultado.impactosTotalesCampana;
};

/**
 * Duraciones de trayecto típicas por tipo de circuito
 */
export const DURACION_TRAYECTO_POR_TIPO = {
  ascensores: 45, // Tiempo promedio en ascensor
  totems: 30, // Tiempo de exposición a tótem (circulación)
  espera: 60, // Tiempo en sala de espera
  cowork: 45, // Similar a ascensor
  default: 45,
};
